import { MenuItem, Text } from "@chakra-ui/react";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { Glossary } from "src/components/Text/Glossary";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canAcceptOffers } from "../validations/acceptOffers";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkOffer } from "./useEnqueueBulkOffer";

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Accepting this offer will decline any previously accepted offers from the selected student.",
    "Accepting these offers will decline any previously accepted offers from the selected students."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, accept offer", "Yes, accept offers");

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
}

export const BulkAcceptOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkOffer = useEnqueueBulkOffer();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "ACCEPT_OFFER",
      bulk: true,
      count,
      async: true,
    });

    if (
      !(await confirm({
        header: (
          <Text>{`Accept ${count} ${maybePluralize(count, "offer")}?`}</Text>
        ),
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      }))
    ) {
      trackFormAction({
        verb: "Canceled",
        actionType: "ACCEPT_OFFER",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "ACCEPT_OFFER",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkOffer({ actionType: "ACCEPT_OFFER" });
  };

  return (
    <WithUserPermissions permissions={["form:update"]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canAcceptOffers(selectedFormSchoolStatuses, selectedFormSchools)
        }
      >
        Accept offers
      </MenuItem>
      {confirmationDialog}
    </WithUserPermissions>
  );
};
